/**
 * Core's SnapshotStore on the web and in the desktop app: what opens at once (Mail's inbox, Home's digest, the threads I've
 * read), one IndexedDB entry per key (idb-keyval) outside the query cache, which is written whole and would carry every
 * message body on each change. Everything is read once before the UI mounts, so `read` stays synchronous for the first
 * frame; core bounds what's kept. It goes at sign-out and when another person opens, and a workspace's once I've left
 * it. Nothing here logs: it holds mail.
 */
import { clear, createStore, del, entries, set, type UseStore } from "idb-keyval";
import type { SnapshotStore } from "@bundle/core";
import { STORAGE_NAMESPACE } from "../env.ts";

export class WebSnapshots implements SnapshotStore {
  private readonly files = new Map<string, string>();
  private readonly db: UseStore;

  constructor(db: UseStore = createStore(`${STORAGE_NAMESPACE}.snapshots`, "files")) {
    this.db = db;
  }

  /** Reads what's on the device. IndexedDB refused (a private window): the app starts from the server. */
  async hydrate(): Promise<void> {
    try {
      for (const [key, value] of await entries<string, string>(this.db)) this.files.set(key, value);
    } catch {
      this.files.clear();
    }
  }

  read(key: string): string | undefined {
    return this.files.get(key);
  }

  write(key: string, encode: () => string): void {
    const value = encode();
    this.files.set(key, value);
    void set(key, value, this.db).catch(() => undefined);
  }

  remove(key: string): void {
    this.files.delete(key);
    void del(key, this.db).catch(() => undefined);
  }

  /**
   * Only this person's entries for the workspaces they're in stay, so nobody else's mail is even in memory and a
   * workspace I left goes. Their other workspaces' stay, so the switcher opens them at once too. Keys end in
   * `.{ws}.{user}`, or hold it.
   */
  keepWorkspaces(userId: string, wsIds: readonly string[]): void {
    const mine = wsIds.map((ws) => `.${ws}.${userId}`);
    for (const key of this.files.keys()) if (!mine.some((m) => key.endsWith(m) || key.includes(`${m}.`))) this.remove(key);
  }

  /** Sign-out: everything. */
  async clear(): Promise<void> {
    this.files.clear();
    await clear(this.db).catch(() => undefined);
  }
}
